/**
 * CinematicStage.jsx — DEAD SIGNAL vertical slice
 *
 * Renders the demo's scenes as looping video backgrounds with CSS hotspot
 * overlays. All motion (LEDs, rain, water shimmer, character breathing,
 * cursor blinks, tower beacons) is baked into the MP4 video loops —
 * no CSS cinemagraph animations needed.
 *
 * Scene → video mapping:
 *   ds_craine_car    → brief_car.mp4 ⇄ debrief_car.mp4 (crossfaded by carMode)
 *   ds_helios_lobby  → helios_lobby.mp4
 *   ds_helios_office → hargrove_office.mp4 (establishing)
 *                    → hargrove_interview.mp4 (interview — single video, no overlay)
 *   ds_elevator_up   → elevator_up.mp4 (one-shot cutscene, lobby → office; ding at end)
 *   ds_elevator_down → elevator_down.mp4 (looping bg of a REAL explorable scene out
 *                      of the office — hotspots + nav, NOT a travel cutscene and NOT
 *                      auto-advancing; the descent to the lobby is a black-hold cut,
 *                      no down-travel video)
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useGameStore } from '../state/gameState.js';
import { getScene } from '../content/sceneManifest.js';
import NavigationMenu from './NavigationMenu.jsx';
import HotspotEditor from './HotspotEditor.jsx';
import { HargroveBioscanHUD } from './InterviewStage.jsx';
import { HOTSPOT_COORDS } from '../content/hotspotCoords.js';
import { isHotspotEditorEnabled, mergedSceneCoords, addedHotspotsFor, editedDefsFor } from '../systems/hotspotOverrides.js';

const CINEMATIC_SCENES = {
  ds_craine_car: { kind: 'car' },
  ds_helios_lobby: { kind: 'lobby' },
  ds_helios_office: { kind: 'office' },
  ds_elevator_up: { kind: 'elevator', direction: 'up', loop: false },
  // Down elevator is a real explorable scene (hotspots + nav + editor), not an
  // auto-advancing transition. Up stays the single animated transition.
  ds_elevator_down: { kind: 'elevator', direction: 'down', loop: true, explorable: true },
};

export function isCinematicScene(sceneId) {
  return Object.prototype.hasOwnProperty.call(CINEMATIC_SCENES, sceneId);
}

/**
 * Committed poster still per cinematic MP4 (see public/images/scenes/stills/
 * stills.json). The MP4s are gitignored, so the still is both the instant
 * first-paint (no black rectangle before decode) and the fallback wherever the
 * video can't stream. Keep in sync with stills.json.
 */
const SCENE_STILLS = {
  '/video/brief_car.mp4': '/images/scenes/stills/brief_car.webp',
  '/video/debrief_car.mp4': '/images/scenes/stills/debrief_car.webp',
  '/video/helios_lobby.mp4': '/images/scenes/stills/helios_lobby.webp',
  '/video/hargrove_office.mp4': '/images/scenes/stills/hargrove_office.webp',
  '/video/hargrove_interview.mp4': '/images/scenes/stills/hargrove_interview.webp',
  '/video/elevator_up.mp4': '/images/scenes/stills/elevator_up.webp',
  '/video/elevator_down.mp4': '/images/scenes/stills/elevator_down.webp',
};

/**
 * Dev / inspection toggle. With `?stills` in the URL, CinematicStage renders the
 * committed still in place of each cinematic video, so a scene can be visually
 * inspected without loading the gitignored MP4 loops (heavy, and the cinemagraph
 * motion is not relevant to development). Production play (no param) is
 * unaffected; the MP4s stay the canonical art.
 */
function stillsOnlyMode() {
  return typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('stills');
}

/**
 * Two visual buckets for the cursor aura, SCAN dots, and tooltip:
 *   evidence — yields a collectible (has `discovers`)  → amber ◆
 *   inspect  — anything else you can examine            → cyan  ◇
 * talk + navigate are HUD buttons (not hover hotspots), so they never reach here.
 */
function inspectKind(item) {
  return item && item.discovers ? 'evidence' : 'inspect';
}

/**
 * SCAN mode overlay: a pulsing dot at the geometric center of each examine
 * hotspot, colored by bucket. Reveals what is interactable without painting
 * the regions themselves. Hidden unless the player toggles SCAN on.
 */
function renderScanDots(examineHotspots, sceneCoords) {
  return examineHotspots.map((item) => {
    const c = sceneCoords[item.id];
    if (!c) return null;
    const cx = parseFloat(c.left) + parseFloat(c.width) / 2;
    const cy = parseFloat(c.top) + parseFloat(c.height) / 2;
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
    return (
      <span
        key={item.id}
        className={`scan-dot scan-dot-${inspectKind(item)}`}
        style={{ left: `${cx}%`, top: `${cy}%` }}
        aria-hidden
      />
    );
  });
}

function renderHotspots(hotspotsList, sceneCoords, onHotspotInteract, handleHover, handleUnhover) {
  return hotspotsList.map((item) => {
    const coords = sceneCoords[item.id];
    if (!coords) return null;
    return (
      <div
        key={item.id}
        data-hotspot-id={item.id}
        className={`cine-hotspot cine-hotspot-${inspectKind(item)}`}
        style={{
          position: 'absolute',
          left: coords.left,
          top: coords.top,
          width: coords.width,
          height: coords.height,
          cursor: coords.cursor || 'pointer',
        }}
        onClick={() => onHotspotInteract(item)}
        onMouseEnter={(e) => handleHover(item, e)}
        onMouseLeave={handleUnhover}
      />
    );
  });
}

function renderActionButtons(actionButtons, onHotspotInteract, onNavigate) {
  return (
    <div className="cine-actions">
      {actionButtons.map((a) => (
        <button
          key={a.id}
          type="button"
          className={`cine-action cine-action-${a._kind}`}
          onClick={() => onHotspotInteract(a)}
        >
          <span className="cine-action-caret">▸</span> {a.label}
        </button>
      ))}
      <NavigationMenu onNavigate={onNavigate} />
    </div>
  );
}

/** Video background layer. Plays when visible, pauses when hidden. */
function VideoLayer({ src, visible, loop = true, onEnded, onBlocked, onReady, poster, className = '' }) {
  const videoRef = useRef(null);
  const stillsOnly = stillsOnlyMode();

  // React's `muted` JSX prop doesn't reliably set the DOM .muted property on all
  // browsers — the attribute and property diverge. Set it imperatively so the
  // browser treats the media as muted and allows autoplay without a user gesture.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  // Report the first paintable frame so the transition cover can lift on the
  // picture, not on a blind timer. Only the visible layer signals readiness.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !visible || !onReady) return;
    if (video.readyState >= 2) { onReady(); return; }
    const fire = () => onReady();
    video.addEventListener('loadeddata', fire, { once: true });
    return () => video.removeEventListener('loadeddata', fire);
  }, [visible, onReady, src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!visible) { video.pause(); return; }

    function tryPlay() {
      // Reset position inside tryPlay so we don't call currentTime = 0 before
      // the video has any data — that can abort the canplay event on some browsers.
      video.currentTime = 0;
      const p = video.play();
      if (p) p.catch(() => {
        onBlocked?.();
        const retry = () => {
          video.play().catch(() => {});
          document.removeEventListener('click', retry);
          document.removeEventListener('keydown', retry);
        };
        document.addEventListener('click', retry, { once: true });
        document.addEventListener('keydown', retry, { once: true });
      });
    }

    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener('canplay', tryPlay, { once: true });
      return () => video.removeEventListener('canplay', tryPlay);
    }
  }, [visible, onBlocked]);

  // ?stills inspection mode: show the committed still in place of the video.
  if (stillsOnly && poster) {
    return (
      <img
        src={poster}
        alt=""
        aria-hidden
        className={`cine-bg ${className}`}
        style={{ opacity: visible ? 1 : 0, visibility: visible ? 'visible' : 'hidden' }}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      src={src}
      poster={poster}
      muted
      playsInline
      loop={loop}
      preload="auto"
      onEnded={onEnded}
      className={`cine-bg ${className}`}
      style={{ opacity: visible ? 1 : 0, visibility: visible ? 'visible' : 'hidden' }}
    />
  );
}

export default function CinematicStage({ onHotspotInteract, onNavigate, onElevatorComplete, onBioscanClick, onSceneReady, chromeless = false, scanMode: scanModeProp }) {
  const sceneId = useGameStore((s) => s.sceneId);
  const carMode = useGameStore((s) => s.carMode);
  const sceneReady = useGameStore((s) => s.sceneReady);
  const interviewActive = useGameStore((s) => s.interview.active);
  const bioscan = useGameStore((s) => s.interview.bioscan);
  const contradictions = useGameStore((s) => s.interview.contradictions);
  const heldEvidence = useGameStore((s) => s.evidence);
  const radioOn = useGameStore((s) => s.radioOn);
  const [hoveredSpot, setHoveredSpot] = useState(null);
  const [labelPos, setLabelPos] = useState({ x: 0, y: 0 });
  const [cursorPt, setCursorPt] = useState({ x: 0, y: 0 });
  // Coarse pointers (phones/tablets) have no hover, so a player can't sweep to
  // discover the invisible hotspots. Boot SCAN on for touch so the dots reveal
  // what's tappable; the SCAN toggle still lets them turn it off. Desktop: off.
  const [internalScanMode, setInternalScanMode] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches,
  );
  // chromeless mode: the v3 left panel owns the SCAN toggle and passes scanMode in.
  const scanMode = scanModeProp !== undefined ? scanModeProp : internalScanMode;
  const [coordsRev, setCoordsRev] = useState(0);
  const viewportRef = useRef(null);
  const editorEnabled = isHotspotEditorEnabled();
  const cfg = CINEMATIC_SCENES[sceneId];
  const scene = cfg ? getScene(sceneId) : null;

  // The MP4 that is actually on screen for this scene/state. Drives both the
  // poster preload (below) and the poster passed to each VideoLayer.
  const visibleVideoSrc = useMemo(() => {
    if (!cfg) return null;
    switch (cfg.kind) {
      case 'car': return carMode === 'debrief' ? '/video/debrief_car.mp4' : '/video/brief_car.mp4';
      case 'lobby': return '/video/helios_lobby.mp4';
      case 'office': return interviewActive ? '/video/hargrove_interview.mp4' : '/video/hargrove_office.mp4';
      case 'elevator': return `/video/elevator_${cfg.direction}.mp4`;
      default: return null;
    }
  }, [cfg, carMode, interviewActive]);

  // Lift the transition cover as soon as the destination scene has SOMETHING to
  // show: the committed poster still (which loads even where the gitignored MP4
  // cannot stream) decodes first in the common case, so the player never waits
  // on the cover. The visible VideoLayer also calls onReady on its first frame;
  // markSceneReady is idempotent, and the store keeps a safety cap besides.
  useEffect(() => {
    if (!onSceneReady) return;
    const still = visibleVideoSrc ? SCENE_STILLS[visibleVideoSrc] : null;
    if (!still) { onSceneReady(); return; }
    const img = new Image();
    let done = false;
    const fire = () => { if (!done) { done = true; onSceneReady(); } };
    img.onload = fire;
    img.onerror = fire;
    img.src = still;
    if (img.complete) fire();
    return () => { img.onload = null; img.onerror = null; };
  }, [visibleVideoSrc, onSceneReady]);

  useEffect(() => { setHoveredSpot(null); }, [sceneId]);

  // When the Hotspot Editor saves an override, re-render so the live hotspots track it.
  useEffect(() => {
    if (!editorEnabled) return;
    const bump = () => setCoordsRev((n) => n + 1);
    window.addEventListener('ds-hotspots-changed', bump);
    return () => window.removeEventListener('ds-hotspots-changed', bump);
  }, [editorEnabled]);

  const clampLabel = useCallback((cx, cy) => {
    const pad = 8;
    const maxW = 220;
    const vpRight = viewportRef.current?.getBoundingClientRect().right ?? window.innerWidth;
    let x = cx + 14;
    let y = cy - 20;
    if (x + maxW + pad > vpRight) x = cx - maxW - 14;
    if (y < pad) y = pad;
    return { x, y };
  }, []);

  const handleHover = useCallback((item, event) => {
    if (interviewActive) return;
    setHoveredSpot(item);
    if (event) {
      setLabelPos(clampLabel(event.clientX, event.clientY));
      setCursorPt({ x: event.clientX, y: event.clientY });
    }
  }, [interviewActive, clampLabel]);

  const handleUnhover = useCallback(() => {
    setHoveredSpot(null);
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (hoveredSpot && event) {
      setLabelPos(clampLabel(event.clientX, event.clientY));
      setCursorPt({ x: event.clientX, y: event.clientY });
    }
  }, [hoveredSpot, clampLabel]);

  // Live label / Craine-comment overrides from the Hotspot Editor (empty for viewers).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editedDefs = useMemo(
    () => (editorEnabled ? editedDefsFor(sceneId) : {}),
    [editorEnabled, sceneId, coordsRev],
  );

  // Visible action buttons: talk hotspots only (navigation menu replaces exit buttons)
  const actionButtons = useMemo(() => {
    if (!scene || interviewActive) return [];
    return (scene.hotspots || [])
      .filter((h) => h.type === 'talk')
      .map((h) => ({ ...h, ...(editedDefs[h.id] || {}), _kind: 'talk' }));
  }, [scene, interviewActive, editedDefs]);

  // Inspection targets authored live in the Hotspot Editor (empty for viewers).
  // An editor-added spot whose id has since been committed into the scene file
  // is dropped here: the committed definition is authoritative, and keeping
  // both renders the hotspot (and its SCAN dot) twice under duplicate keys.
  // Live tweaks to a committed spot flow through editedDefs, not added.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const addedSpots = useMemo(() => {
    if (!editorEnabled) return [];
    const committed = new Set((scene?.hotspots || []).map((h) => h.id));
    return addedHotspotsFor(sceneId).filter((h) => !committed.has(h.id));
  }, [editorEnabled, sceneId, scene, coordsRev]);

  // An evidence hotspot is "collected" once every id in its `discovers` is in
  // the player's held evidence. Collected evidence leaves the scene entirely:
  // no hotspot, no SCAN dot, no aura or tooltip. Inspect targets without
  // `discovers` are not collectible and always remain.
  const isCollected = useCallback((h) => {
    if (!h || !h.discovers) return false;
    const ids = Array.isArray(h.discovers) ? h.discovers : [h.discovers];
    return ids.length > 0 && ids.every((id) => heldEvidence.includes(id));
  }, [heldEvidence]);

  // A collected hotspot's div unmounts without firing mouseleave, so clear a
  // stranded aura/tooltip when the hovered spot is the one just collected.
  useEffect(() => {
    setHoveredSpot((cur) => (cur && isCollected(cur) ? null : cur));
  }, [isCollected]);

  // Invisible hover hotspots: EXAMINE items only (plus any editor-authored
  // targets), with live label/comment edits applied. Talk + navigate are
  // surfaced as HUD buttons (action buttons / NavigationMenu), never as regions.
  const hotspotsList = useMemo(() => {
    if (!scene || interviewActive) return [];
    const sceneExamine = (scene.hotspots || [])
      .filter((h) => h.type === 'examine')
      .map((h) => {
        const merged = { ...h, ...(editedDefs[h.id] || {}), _kind: h.type };
        // Car debrief: a spot that reads differently after the interview carries
        // debriefLabel / debriefDescription (e.g. the spire becomes the Night Owl).
        // The resolved object also flows to onHotspotInteract, so the click line
        // swaps with the hover label.
        if (carMode === 'debrief' && merged.debriefDescription) {
          return { ...merged, label: merged.debriefLabel || merged.label, description: merged.debriefDescription };
        }
        return merged;
      });
    return [...sceneExamine, ...addedSpots].filter((h) => !isCollected(h));
  }, [scene, interviewActive, addedSpots, editedDefs, isCollected, carMode]);

  // SCAN dots mark the examine hotspots (the things you inspect in-scene).
  const scanHotspots = useMemo(() => {
    if (!scene) return [];
    return [...(scene.hotspots || []).filter((h) => h.type === 'examine'), ...addedSpots]
      .filter((h) => !isCollected(h));
  }, [scene, addedSpots, isCollected]);

  // Committed coords, with any live editor overrides merged on top (no-op for viewers).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sceneCoords = useMemo(() => mergedSceneCoords(HOTSPOT_COORDS, sceneId), [sceneId, coordsRev]);

  // ── Elevator auto-advance (up cutscene only) ───────
  // Only the non-explorable ascent cutscene (ds_elevator_up) reaches here; the
  // descent (ds_elevator_down) is explorable and bails below. Normal advance is
  // the <video> onEnded callback at the end of elevator_up. This timer is the
  // safety fallback: if the video never plays (autoplay blocked + no interaction)
  // it advances after the duration + grace so the game never hangs on black.
  useEffect(() => {
    const elevCfg = CINEMATIC_SCENES[sceneId];
    if (!elevCfg || elevCfg.kind !== 'elevator' || elevCfg.explorable) return; // explorable elevators wait for the player
    const duration = elevCfg.loop ? 16000 : 14000; // elevator_up=11s + 3s grace (loop branch unused: the only loop scene is explorable)
    const timer = setTimeout(() => {
      onElevatorComplete?.();
    }, duration);
    return () => clearTimeout(timer);
  }, [sceneId, onElevatorComplete]);

  if (!cfg) return null;

  // SCAN toggle rendered inside each 16:9 container so it overlays scene
  // content, not the letterbox/pillarbox area surrounding it.
  const scanToggle = !interviewActive && !chromeless && (
    <button
      type="button"
      className={`scan-toggle ${scanMode ? 'on' : ''}`}
      onClick={() => setInternalScanMode((v) => !v)}
      aria-pressed={scanMode}
      title="Reveal interactable points in this scene"
    >
      <span className="scan-ico">◎</span> SCAN
    </button>
  );

  // Bioscan HUD rendered inside each 16:9 container so it overlays the scene
  // content rather than the surrounding black bars on wide screens.
  const contradiction = contradictions?.length > 0 ? contradictions[contradictions.length - 1] : null;
  const bioscanOverlay = !chromeless && interviewActive && (
    <HargroveBioscanHUD row={bioscan} onBioscanClick={onBioscanClick} contradiction={contradiction} />
  );

  // chromeless: the v3 HUD frame owns the action bar + NavigationMenu, so the
  // in-scene talk buttons + nav are suppressed here (the scene keeps only its
  // hotspots, scan dots, and the SCAN/BIO-SCAN overlays).
  const actionsEl = chromeless ? null : renderActionButtons(actionButtons, onHotspotInteract, onNavigate);

  return (
    <div ref={viewportRef} className={`scene-viewport cine-viewport cine-${cfg.kind}`} onMouseMove={handleMouseMove}>
      {cfg.kind === 'car' && (
        <div className="cine-container-16-9">
          <VideoLayer src="/video/brief_car.mp4" poster={SCENE_STILLS['/video/brief_car.mp4']} onReady={onSceneReady} visible={carMode !== 'debrief'} />
          <VideoLayer src="/video/debrief_car.mp4" poster={SCENE_STILLS['/video/debrief_car.mp4']} onReady={onSceneReady} visible={carMode === 'debrief'} />
          {renderHotspots(hotspotsList, sceneCoords, onHotspotInteract, handleHover, handleUnhover)}
          {radioOn && (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                left: '43%',
                top: '76%',
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#ffb24d',
                boxShadow: '0 0 8px 3px rgba(255,178,77,0.65)',
                pointerEvents: 'none',
                zIndex: 50,
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
          {scanMode && sceneReady && renderScanDots(scanHotspots, sceneCoords)}
          {actionsEl}
          {scanToggle}
          {bioscanOverlay}
          {editorEnabled && <HotspotEditor sceneId={sceneId} />}
        </div>
      )}
      {cfg.kind === 'lobby' && (
        <div className="cine-container-16-9">
          <VideoLayer src="/video/helios_lobby.mp4" poster={SCENE_STILLS['/video/helios_lobby.mp4']} onReady={onSceneReady} visible />
          {renderHotspots(hotspotsList, sceneCoords, onHotspotInteract, handleHover, handleUnhover)}
          {scanMode && sceneReady && renderScanDots(scanHotspots, sceneCoords)}
          {actionsEl}
          {scanToggle}
          {bioscanOverlay}
          {editorEnabled && <HotspotEditor sceneId={sceneId} />}
        </div>
      )}
      {cfg.kind === 'office' && (
        <div className="cine-container-16-9">
          <VideoLayer src="/video/hargrove_office.mp4" poster={SCENE_STILLS['/video/hargrove_office.mp4']} onReady={onSceneReady} visible={!interviewActive} />
          <VideoLayer src="/video/hargrove_interview.mp4" poster={SCENE_STILLS['/video/hargrove_interview.mp4']} onReady={onSceneReady} visible={interviewActive} />
          {renderHotspots(hotspotsList, sceneCoords, onHotspotInteract, handleHover, handleUnhover)}
          {scanMode && sceneReady && !interviewActive && renderScanDots(scanHotspots, sceneCoords)}
          {actionsEl}
          {scanToggle}
          {bioscanOverlay}
          {editorEnabled && !interviewActive && <HotspotEditor sceneId={sceneId} />}
        </div>
      )}
      {cfg.kind === 'elevator' && (
        <div className="cine-container-16-9">
          <VideoLayer
            src={`/video/elevator_${cfg.direction}.mp4`}
            poster={SCENE_STILLS[`/video/elevator_${cfg.direction}.mp4`]}
            onReady={onSceneReady}
            visible
            loop={cfg.loop}
            onEnded={!cfg.loop ? onElevatorComplete : undefined}
          />
          {cfg.explorable && (
            <>
              {renderHotspots(hotspotsList, sceneCoords, onHotspotInteract, handleHover, handleUnhover)}
              {scanMode && sceneReady && renderScanDots(scanHotspots, sceneCoords)}
              {actionsEl}
              {scanToggle}
            </>
          )}
        </div>
      )}

      {/* Cursor aura — blooms at the pointer ONLY while over a hotspot, colored by type. */}
      {hoveredSpot && !interviewActive && (
        <div
          className={`hotspot-aura hotspot-aura-${inspectKind(hoveredSpot)}`}
          style={{ position: 'fixed', left: cursorPt.x, top: cursorPt.y, pointerEvents: 'none', zIndex: 990 }}
          aria-hidden
        />
      )}

      {/* Cursor-following Tooltip Label (stays in viewport, not 16:9 container) */}
      {hoveredSpot && (
        <div
          className={`hotspot-label hotspot-label-${inspectKind(hoveredSpot)}`}
          style={{
            position: 'fixed',
            left: labelPos.x,
            top: labelPos.y,
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'nowrap',
          }}
        >
          {hoveredSpot.discovers
            ? <span className="hotspot-tag hotspot-tag-evidence">{'◆'} </span>
            : <span className="hotspot-tag hotspot-tag-inspect">{'◇'} </span>}
          {hoveredSpot.label}
        </div>
      )}
    </div>
  );
}
